import { useTranslation } from 'react-i18next';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import { detectLanguageFromFileName } from '@web/app/shared/utils/languageDetector';
import { X } from 'lucide-react';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from '@codes/ui-kit';

interface FileViewerDialogProps {
    open: boolean;
    onOpenChange: (val: boolean) => void;
    fileName: string;
    fileContent: string | null;
    isLoading: boolean;
    error?: string;
}

export const FileViewerDialog = ({
    open,
    onOpenChange,
    fileName,
    fileContent,
    isLoading,
    error,
}: FileViewerDialogProps) => {
    const { t } = useTranslation();
    const language = detectLanguageFromFileName(fileName);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                className="overflow-y-scroll sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw]"
                aria-describedby={undefined}
                preventDismiss={false}
            >
                <SheetHeader className="justify-between w-full flex" aria-describedby={undefined}>
                    <SheetTitle className="truncate pr-8">{fileName}</SheetTitle>
                    <SheetClose>
                        <X className="w-5 h-5" />
                    </SheetClose>
                </SheetHeader>

                <div className="py-6">
                    {isLoading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-text-500">{t('common.loading')}</div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="text-red-800 font-medium">{t('aiStudio.fileViewer.error')}</div>
                            <div className="text-red-600 text-sm mt-1">{error}</div>
                        </div>
                    )}

                    {!isLoading && !error && fileContent && (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="bg-box px-4 py-2 border-b">
                                <div className="text-sm font-medium text-sub-title">
                                    {t('aiStudio.fileViewer.content')}
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <SyntaxHighlighter
                                    language={language}
                                    style={vscDarkPlus}
                                    showLineNumbers
                                    wrapLines
                                    customStyle={{
                                        margin: 0,
                                        borderRadius: 0,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {fileContent}
                                </SyntaxHighlighter>
                            </div>
                        </div>
                    )}

                    {!isLoading && !error && !fileContent && (
                        <div className="text-center py-12 text-text-500">{t('aiStudio.fileViewer.noContent')}</div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};
